import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TextField, Avatar, Chip,
  CircularProgress, Alert, Stack, IconButton, Tooltip,
  Paper, Divider
} from '@mui/material';
import {
  Close, AutoAwesome, Send, Refresh, Star, LocationOn,
  CheckCircle, Work, VerifiedUser
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import DialogCloseButton from '../common/DialogCloseButton';
import axios from 'axios';
import { brand, border } from '../../theme/colors';

/**
 * AIAssistedHireDialog
 * Unified component for hiring contractors or verifiers with AI-generated messages
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close handler
 * @param {Object} props.targetUser - The contractor/verifier to hire
 * @param {string} props.userRole - 'contractor' | 'verifier_professional' | 'verifier_general'
 * @param {Object} props.project - Project context
 * @param {function} props.onSuccess - Success callback
 */
const AIAssistedHireDialog = ({
  open,
  onClose,
  targetUser,
  userRole = 'contractor',
  project,
  onSuccess
}) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [improving, setImproving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [improvementStyle, setImprovementStyle] = useState('');

  // Role-specific configuration
  const roleConfig = {
    contractor: {
      title: 'Hire Contractor',
      subtitle: 'Send a hire request with an AI-generated message',
      icon: Work,
      color: '#2196f3', // Blue
      gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      apiEndpoint: '/api/hires/ai-assisted',
    },
    verifier_professional: {
      title: 'Hire Professional Verifier',
      subtitle: 'Send a hire request with an AI-generated message',
      icon: VerifiedUser,
      color: brand.primary,
      gradient: brand.gradient,
      apiEndpoint: '/api/contractor/verifier-hires',
    },
    verifier_general: {
      title: 'Hire Verifier',
      subtitle: 'Send a hire request with an AI-generated message',
      icon: VerifiedUser,
      color: brand.primary,
      gradient: brand.gradient,
      apiEndpoint: '/api/contractor/verifier-hires',
    }
  };

  const config = roleConfig[userRole] || roleConfig.contractor;
  const RoleIcon = config.icon;

  useEffect(() => {
    if (open && !message) {
      // Auto-generate message when dialog opens
      handleGenerateMessage();
    }
  }, [open]);

  const handleGenerateMessage = async () => {
    if (!project || !targetUser) {
      setError('Project and user information required');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      let response;

      if (userRole === 'contractor') {
        // Generate contractor hire message
        response = await axios.post('/api/hires/generate-message', {
          contractorId: targetUser.id || targetUser.user_id,
          contractorName: targetUser.full_name || targetUser.name,
          contractorSpecialties: targetUser.specialties || targetUser.contractor_type || [],
          projectId: project.id,
          projectTitle: project.title || project.name,
          projectType: project.type || project.category,
          projectDescription: project.description,
          projectLocation: project.location,
          urgency: project.urgency || 'normal'
        });
      } else {
        // Generate verifier hire message (existing endpoint)
        response = await axios.post('/api/contractor/verifier-marketplace/generate-message', {
          verifierId: targetUser.user_id || targetUser.id,
          verifierName: targetUser.name,
          verifierSpecializations: targetUser.specializations || [],
          projectId: project.id,
          projectType: project.type || project.category,
          projectDescription: project.description,
          projectLocation: project.location,
          urgency: project.urgency || 'normal'
        });
      }

      setMessage(response.data.data.message);
    } catch (err) {
      console.error('Error generating message:', err);
      setError('Failed to generate message. Please write one manually.');
      // Set a default fallback message
      setMessage(getFallbackMessage());
    } finally {
      setGenerating(false);
    }
  };

  const getFallbackMessage = () => {
    const userName = targetUser?.full_name || targetUser?.name || 'there';
    const projectName = project?.title || project?.name || 'this project';
    const projectType = project?.type || project?.category || 'project';
    const location = project?.location || 'your area';

    if (userRole === 'contractor') {
      return `Hi ${userName},\n\nI'm working on a ${projectType} in ${location} and would like to hire you for this project: "${projectName}".\n\nBased on your experience and profile, I believe you'd be a great fit. Please let me know if you're available to discuss this opportunity.\n\nLooking forward to working with you!\n\nBest regards`;
    } else {
      return `Hi ${userName},\n\nI'm working on a ${projectType} project in ${location} and would like to hire you as the verifier for "${projectName}".\n\nPlease let me know if you're available.\n\nThank you!`;
    }
  };

  const handleImproveMessage = async (style) => {
    if (!message) {
      setError('Please enter a message first');
      return;
    }

    setImproving(true);
    setImprovementStyle(style);
    setError('');

    try {
      let response;

      if (userRole === 'contractor') {
        // Contractor endpoint
        response = await axios.post('/api/hires/improve-message', {
          message: message,
          style: style
        });
        setMessage(response.data.data.improvedMessage);
      } else {
        // Verifier endpoint (different parameter names and response structure)
        response = await axios.post('/api/contractor/verifier-marketplace/improve-message', {
          originalMessage: message,
          improvementType: style
        });
        setMessage(response.data.improved);
      }
    } catch (err) {
      console.error('Error improving message:', err);
      setError('Failed to improve message. Your original message is preserved.');
    } finally {
      setImproving(false);
      setImprovementStyle('');
    }
  };

  const handleSendHireRequest = async () => {
    if (!message || message.trim().length === 0) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let response;

      if (userRole === 'contractor') {
        // Send contractor hire request
        response = await axios.post('/api/hires', {
          projectId: project.id,
          contractorId: targetUser.id,
          message: message.trim()
        });
      } else {
        // Send verifier hire request (existing endpoint)
        response = await axios.post('/api/contractor/verifier-hires', {
          verifierId: targetUser.user_id,
          projectId: project.id,
          message: message.trim()
        });
      }

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(response.data.data);
        }
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error sending hire request:', err);
      setError(err.response?.data?.message || 'Failed to send hire request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMessage('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  // Don't render if required props are missing
  if (!targetUser || !project) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      {/* Header with role-specific gradient */}
      <DialogTitle
        sx={{
          background: config.gradient,
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
              <RoleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {config.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              {config.subtitle}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Success Message */}
        {success && (
          <Alert
            severity="success"
            icon={<CheckCircle />}
            sx={{ mb: 3 }}
          >
            Hire request sent successfully! {targetUser.full_name || targetUser.name} will be notified.
          </Alert>
        )}

        {/* Error Alert */}
        {error && !success && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Target User Info Card */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#f9f9f9',
            border: `1px solid ${border.default}`,
            borderRadius: 2,
            p: 2.5,
            mb: 3
          }}
        >
          <Box display="flex" alignItems="center">
            <Avatar
              src={targetUser.profile_image_url || targetUser.avatar}
              alt={targetUser.name || targetUser.full_name}
              sx={{ width: 56, height: 56, mr: 2 }}
            >
              {(targetUser.name || targetUser.full_name)?.charAt(0)}
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="h6" fontWeight={700}>
                {targetUser.full_name || targetUser.name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                {targetUser.tier && (
                  <Chip
                    label={targetUser.tier.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: config.color,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                )}
                {targetUser.contractor_type && (
                  <Chip
                    label={targetUser.contractor_type}
                    size="small"
                    sx={{
                      backgroundColor: config.color,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                )}
                {(targetUser.avg_rating > 0 || targetUser.rating > 0) && (
                  <Box display="flex" alignItems="center">
                    <Star sx={{ fontSize: 16, color: '#ffc107', mr: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">
                      {parseFloat(targetUser.avg_rating || targetUser.rating).toFixed(1)}
                    </Typography>
                  </Box>
                )}
                {(targetUser.base_city || targetUser.location) && (
                  <Box display="flex" alignItems="center">
                    <LocationOn sx={{ fontSize: 16, color: '#666', mr: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">
                      {targetUser.base_city || targetUser.location}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </Paper>

        {/* Project Info */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: userRole === 'contractor' ? '#e3f2fd' : '#f3f0ff',
            border: userRole === 'contractor' ? '1px solid #bbdefb' : '1px solid #e0d7ff',
            borderRadius: 2,
            p: 2,
            mb: 3
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color={config.color} gutterBottom>
            Project: {project.name || project.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {project.type || project.category} • {project.location}
          </Typography>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Message Section */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Your Message
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Generate new message with AI">
                <IconButton
                  size="small"
                  onClick={handleGenerateMessage}
                  disabled={generating || loading}
                  sx={{ color: config.color }}
                >
                  {generating ? <CircularProgress size={20} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Message TextField */}
          <TextField
            fullWidth
            multiline
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Your message to the ${userRole === 'contractor' ? 'contractor' : 'verifier'}...`}
            disabled={generating || loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white'
              }
            }}
          />

          {/* AI Improvement Buttons */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              <AutoAwesome sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              Improve with AI:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleImproveMessage('shorter')}
                disabled={improving || loading || !message}
                startIcon={improving && improvementStyle === 'shorter' ? <CircularProgress size={14} /> : null}
                sx={{
                  textTransform: 'none',
                  borderColor: config.color,
                  color: config.color,
                  '&:hover': {
                    borderColor: config.color,
                    backgroundColor: userRole === 'contractor' ? '#e3f2fd' : '#f3f0ff'
                  }
                }}
              >
                Make Shorter
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleImproveMessage('formal')}
                disabled={improving || loading || !message}
                startIcon={improving && improvementStyle === 'formal' ? <CircularProgress size={14} /> : null}
                sx={{
                  textTransform: 'none',
                  borderColor: config.color,
                  color: config.color,
                  '&:hover': {
                    borderColor: config.color,
                    backgroundColor: userRole === 'contractor' ? '#e3f2fd' : '#f3f0ff'
                  }
                }}
              >
                More Formal
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleImproveMessage('friendly')}
                disabled={improving || loading || !message}
                startIcon={improving && improvementStyle === 'friendly' ? <CircularProgress size={14} /> : null}
                sx={{
                  textTransform: 'none',
                  borderColor: config.color,
                  color: config.color,
                  '&:hover': {
                    borderColor: config.color,
                    backgroundColor: userRole === 'contractor' ? '#e3f2fd' : '#f3f0ff'
                  }
                }}
              >
                More Friendly
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleImproveMessage('urgent')}
                disabled={improving || loading || !message}
                startIcon={improving && improvementStyle === 'urgent' ? <CircularProgress size={14} /> : null}
                sx={{
                  textTransform: 'none',
                  borderColor: config.color,
                  color: config.color,
                  '&:hover': {
                    borderColor: config.color,
                    backgroundColor: userRole === 'contractor' ? '#e3f2fd' : '#f3f0ff'
                  }
                }}
              >
                Add Urgency
              </Button>
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${border.default}` }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSendHireRequest}
          disabled={loading || !message || message.trim().length === 0}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
          sx={{
            background: config.gradient,
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              opacity: 0.9
            }
          }}
        >
          Send Hire Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIAssistedHireDialog;
